import { Button, PlusIcon, Select, TextInput } from './ui';
import { TrashIcon } from './ui/Icons';
import { TableIconButton } from './TableIconAction';
import { t } from '../i18n';
import { formatMoney } from '../utils/format';
import { cn } from '../lib/utils';

export type EditableAllocation = {
  key: string;
  invoiceId: string;
  invoiceAmount: string;
  cashAmount: string;
};

type InvoiceOption = {
  id: number;
  invoiceNo: string;
  label?: string;
};

type PaymentAllocationsEditorProps = {
  allocations: EditableAllocation[];
  onChange: (allocations: EditableAllocation[]) => void;
  invoices: InvoiceOption[];
  disabled?: boolean;
  paymentInvoiceTotal: number;
  paymentCashTotal: number;
};

export function createEmptyAllocation(): EditableAllocation {
  return {
    key: `alloc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    invoiceId: '',
    invoiceAmount: '',
    cashAmount: '',
  };
}

export function PaymentAllocationsEditor({
  allocations,
  onChange,
  invoices,
  disabled = false,
  paymentInvoiceTotal,
  paymentCashTotal,
}: PaymentAllocationsEditorProps) {
  const labels = t().payments;

  const allocatedInvoice = allocations.reduce(
    (sum, row) => sum + (parseLocaleAmount(row.invoiceAmount) || 0),
    0,
  );
  const allocatedCash = allocations.reduce(
    (sum, row) => sum + (parseLocaleAmount(row.cashAmount) || 0),
    0,
  );

  const remainingInvoice = paymentInvoiceTotal - allocatedInvoice;
  const remainingCash = paymentCashTotal - allocatedCash;

  function updateRow(key: string, patch: Partial<EditableAllocation>) {
    onChange(
      allocations.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  }

  function removeRow(key: string) {
    onChange(allocations.filter((row) => row.key !== key));
  }

  const invoiceOptions = invoices.map((invoice) => ({
    value: String(invoice.id),
    label: invoice.label ?? invoice.invoiceNo,
  }));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">
          {labels.allocationsSection}
        </h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || invoices.length === 0}
          onClick={() => onChange([...allocations, createEmptyAllocation()])}
        >
          <PlusIcon className="h-3.5 w-3.5" />
          {labels.addAllocation}
        </Button>
      </div>

      <p className="text-xs text-ink-muted">{labels.allocationsHint}</p>

      {allocations.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line px-3 py-4 text-sm text-ink-muted">
          {labels.unallocated}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-muted text-left text-xs text-ink-muted">
              <tr>
                <th className="px-3 py-2">{labels.invoice}</th>
                <th className="px-3 py-2 text-right">{t().financial.invoice}</th>
                <th className="px-3 py-2 text-right">{t().financial.cash}</th>
                <th className="w-12 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {allocations.map((row) => (
                <tr key={row.key} className="border-t border-line">
                  <td className="px-3 py-2">
                    <Select
                      value={row.invoiceId}
                      options={invoiceOptions}
                      placeholder={labels.selectInvoice}
                      showClearButton={false}
                      disabled={disabled}
                      size="sm"
                      onChange={(e) =>
                        updateRow(row.key, { invoiceId: e.target.value })
                      }
                    />
                  </td>
                  <td className="px-3 py-2">
                    <TextInput
                      value={row.invoiceAmount}
                      onChange={(e) =>
                        updateRow(row.key, { invoiceAmount: e.target.value })
                      }
                      placeholder={t().common.placeholders.amount}
                      showClearButton={false}
                      size="sm"
                      disabled={disabled}
                      inputMode="decimal"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <TextInput
                      value={row.cashAmount}
                      onChange={(e) =>
                        updateRow(row.key, { cashAmount: e.target.value })
                      }
                      placeholder={t().common.placeholders.amount}
                      showClearButton={false}
                      size="sm"
                      disabled={disabled}
                      inputMode="decimal"
                    />
                  </td>
                  <td className="px-2 py-2 text-center">
                    <TableIconButton
                      label={t().common.cancel}
                      disabled={disabled}
                      onClick={() => removeRow(row.key)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </TableIconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid gap-2 text-sm sm:grid-cols-2">
        <div className="rounded-lg border border-line bg-surface-muted/40 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-ink-faint">
            {labels.allocated}
          </div>
          <div className="mt-1 flex justify-between">
            <span>{t().financial.invoice}</span>
            <span className="tabular-nums">{formatMoney(allocatedInvoice)}</span>
          </div>
          <div className="flex justify-between">
            <span>{t().financial.cash}</span>
            <span className="tabular-nums">{formatMoney(allocatedCash)}</span>
          </div>
        </div>
        <div
          className={cn(
            'rounded-lg border p-3',
            remainingInvoice < -0.001 || remainingCash < -0.001
              ? 'border-red-300 bg-red-50/50 dark:border-red-500/30 dark:bg-red-950/30'
              : 'border-line bg-surface-muted/40',
          )}
        >
          <div className="text-xs font-medium uppercase tracking-wide text-ink-faint">
            {labels.unallocatedRemainder}
          </div>
          <div className="mt-1 flex justify-between">
            <span>{t().financial.invoice}</span>
            <span className="tabular-nums">{formatMoney(remainingInvoice)}</span>
          </div>
          <div className="flex justify-between">
            <span>{t().financial.cash}</span>
            <span className="tabular-nums">{formatMoney(remainingCash)}</span>
          </div>
        </div>
      </div>

      {invoices.length === 0 ? (
        <p className="text-xs text-ink-muted">{labels.noInvoicesForCustomer}</p>
      ) : null}
    </div>
  );
}

function parseLocaleAmount(value: string): number {
  const normalized = value.trim().replace(/\s/g, '').replace(',', '.');
  if (!normalized) return 0;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}
